import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import request from '@/utils/request';
import API from '@/config/api';
import { REGION_URLS } from '@/config/env';
import type { CompanyProfile, PaymentLog, SuspiciousSurvey } from '@/types/company.types';
import type { UpdateCompanyInfoPayload, SubscriptionActionPayload, ResetPilotDataPayload } from './companyProfileSlice';
import {
  fetchCompanyInfoRequest, fetchCompanyInfoSuccess, fetchCompanyInfoFailure,
  updateCompanyInfoRequest, updateCompanyInfoSuccess, updateCompanyInfoFailure,
  addSubscriptionRequest, addSubscriptionSuccess, addSubscriptionFailure,
  editSubscriptionRequest, editSubscriptionSuccess, editSubscriptionFailure,
  updatePlanRequest, updatePlanSuccess, updatePlanFailure,
  purchaseAddonsRequest, purchaseAddonsSuccess, purchaseAddonsFailure,
  fetchPaymentLogsRequest, fetchPaymentLogsSuccess, fetchPaymentLogsFailure,
  fetchSuspiciousSurveysRequest, fetchSuspiciousSurveysSuccess,
  unblockSurveyRequest, unblockSurveySuccess,
  clearCacheRequest, clearCacheSuccess, clearCacheFailure,
  resetGatewayRequest, resetGatewaySuccess, resetGatewayFailure,
  accountReviewRequest, accountReviewSuccess, accountReviewFailure,
  resetPilotDataRequest, resetPilotDataSuccess, resetPilotDataFailure,
} from './companyProfileSlice';

function* handleFetchCompanyInfo(action: PayloadAction<string>) {
  try {
    const res: { data: { data: CompanyProfile } } = yield call(request.get, API.COMPANY_INFO(action.payload));
    yield put(fetchCompanyInfoSuccess(res.data.data));
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(fetchCompanyInfoFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleUpdateCompanyInfo(action: PayloadAction<UpdateCompanyInfoPayload>) {
  try {
    yield call(request.put, API.COMPANY_UPDATE, action.payload);
    yield put(updateCompanyInfoSuccess(action.payload));
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(updateCompanyInfoFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleAddSubscription(action: PayloadAction<SubscriptionActionPayload>) {
  try {
    yield call(request.post, API.ADD_MANUAL_SUBSCRIPTION, action.payload);
    yield put(addSubscriptionSuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(addSubscriptionFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleEditSubscription(action: PayloadAction<SubscriptionActionPayload>) {
  try {
    yield call(request.put, API.EDIT_MANUAL_SUBSCRIPTION, action.payload);
    yield put(editSubscriptionSuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(editSubscriptionFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleUpdatePlan(action: PayloadAction<SubscriptionActionPayload>) {
  try {
    yield call(request.put, API.UPDATE_COMPANY_PLAN, action.payload);
    yield put(updatePlanSuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(updatePlanFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handlePurchaseAddons(action: PayloadAction<SubscriptionActionPayload>) {
  try {
    yield call(request.post, API.PURCHASE_ADDONS, action.payload);
    yield put(purchaseAddonsSuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(purchaseAddonsFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchPaymentLogs(action: PayloadAction<string>) {
  try {
    const res: { data: { data: { list: PaymentLog[]; totalRecords: number } } } = yield call(request.get, API.PAYMENT_LOGS(action.payload));
    yield put(fetchPaymentLogsSuccess({ list: res.data.data.list, total: res.data.data.totalRecords }));
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(fetchPaymentLogsFailure());
  }
}

function* handleFetchSuspiciousSurveys(action: PayloadAction<string>) {
  try {
    const res: { data: { data: SuspiciousSurvey[] } } = yield call(request.get, API.SUSPICIOUS_SURVEYS(action.payload));
    yield put(fetchSuspiciousSurveysSuccess(res.data.data));
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
  }
}

function* handleUnblockSurvey(action: PayloadAction<{ surveyId: string; companyId: string }>) {
  try {
    yield call(request.post, API.UNBLOCK_SUSPICIOUS_SURVEY, action.payload);
    yield put(unblockSurveySuccess(action.payload.surveyId));
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
  }
}

function* handleClearCache(action: PayloadAction<string>) {
  try {
    const regions = Object.values(REGION_URLS).filter(Boolean);
    for (const regionUrl of regions) {
      yield call(request.post, `${regionUrl}/api/v1/distribution/clearCachedWidgetJS`, { companyId: action.payload });
    }
    yield put(clearCacheSuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(clearCacheFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleResetGateway(action: PayloadAction<string>) {
  try {
    yield call(request.put, API.UPDATE_GATEWAY, { companyId: action.payload });
    yield put(resetGatewaySuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(resetGatewayFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleAccountReview(action: PayloadAction<string>) {
  try {
    yield call(request.post, API.ACCOUNT_REVIEW, { companyId: action.payload });
    yield put(accountReviewSuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(accountReviewFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleResetPilotData(action: PayloadAction<ResetPilotDataPayload>) {
  try {
    yield call(request.post, API.RESET_PILOT_DATA, action.payload);
    yield put(resetPilotDataSuccess());
  } catch (e: unknown) {
    console.error('companyProfile saga error:', e);
    yield put(resetPilotDataFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

export default function* companyProfileSaga() {
  yield takeLatest(fetchCompanyInfoRequest.type, handleFetchCompanyInfo);
  yield takeLatest(updateCompanyInfoRequest.type, handleUpdateCompanyInfo);
  yield takeLatest(addSubscriptionRequest.type, handleAddSubscription);
  yield takeLatest(editSubscriptionRequest.type, handleEditSubscription);
  yield takeLatest(updatePlanRequest.type, handleUpdatePlan);
  yield takeLatest(purchaseAddonsRequest.type, handlePurchaseAddons);
  yield takeLatest(fetchPaymentLogsRequest.type, handleFetchPaymentLogs);
  yield takeLatest(fetchSuspiciousSurveysRequest.type, handleFetchSuspiciousSurveys);
  yield takeLatest(unblockSurveyRequest.type, handleUnblockSurvey);
  yield takeLatest(clearCacheRequest.type, handleClearCache);
  yield takeLatest(resetGatewayRequest.type, handleResetGateway);
  yield takeLatest(accountReviewRequest.type, handleAccountReview);
  yield takeLatest(resetPilotDataRequest.type, handleResetPilotData);
}
